import type { DishCategory } from "../shared/types";
import type { GuestOrderItem } from "./guest-order";

export const ORDER_IMAGE_WIDTH = 1080;
export const ORDER_IMAGE_MIN_HEIGHT = 1440;

const PAPER = "#FBF6E8";
const INK = "#35251F";
const MUTED_INK = "#8B5F4D";
const ACCENT = "#EF512F";
const IMAGE_PADDING = 82;
const GRID_TOP = 530;
const GRID_COLUMNS = 2;
const GRID_GAP_X = 78;
const GRID_GAP_Y = 16;
const SECTION_GAP_Y = 34;
const SECTION_HEADING_HEIGHT = 66;
const CELL_MIN_HEIGHT = 84;
const CELL_LINE_HEIGHT = 40;
const CELL_VERTICAL_PADDING = 34;
const FOOTER_HEIGHT = 620;
const MAX_NAME_CHARS_PER_LINE = 10;

const DISPLAY_FONT = '"Cooper Black", "Rockwell Extra Bold", Georgia, "Times New Roman", serif';
const CHINESE_DISPLAY_FONT = '"Songti SC", STSong, "Noto Serif CJK SC", "Microsoft YaHei", serif';
const BODY_FONT = 'ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif';
const EATING_ILLUSTRATION_PATH = "/assets/menu-eating-cutout-v1.png";
const RUNNER_ILLUSTRATION_PATH = "/assets/menu-bag-runner-cutout-v1.png";

const CATEGORY_LABELS: Record<DishCategory | "custom", string> = {
  staple: "主食",
  meat: "荤菜",
  seafood: "海鲜",
  vegetable: "素菜",
  soup: "汤锅",
  snack: "小吃",
  dessert: "甜品",
  custom: "特别想吃",
};

const CATEGORY_ORDER: Array<DishCategory | "custom"> = [
  "staple",
  "meat",
  "seafood",
  "vegetable",
  "soup",
  "snack",
  "dessert",
  "custom",
];

export interface OrderImageRow {
  items: Array<{ item: GuestOrderItem; lines: string[] }>;
  height: number;
}

export interface OrderImageSection {
  category: DishCategory | "custom";
  label: string;
  rows: OrderImageRow[];
  height: number;
}

export interface OrderImageLayout {
  width: number;
  height: number;
  cellWidth: number;
  sections: OrderImageSection[];
}

export interface GeneratedOrderImage {
  blob: Blob;
  fileName: string;
  height: number;
  width: number;
}

export function splitOrderItemName(name: string, maxChars = MAX_NAME_CHARS_PER_LINE): string[] {
  const chars = Array.from(name);
  const lines: string[] = [];
  for (let index = 0; index < chars.length; index += maxChars) {
    lines.push(chars.slice(index, index + maxChars).join(""));
  }
  return lines.length > 0 ? lines : [""];
}

export function calculateOrderImageLayout(items: readonly GuestOrderItem[]): OrderImageLayout {
  const contentWidth = ORDER_IMAGE_WIDTH - (IMAGE_PADDING * 2);
  const cellWidth = (contentWidth - GRID_GAP_X) / GRID_COLUMNS;
  const sections = CATEGORY_ORDER.flatMap((category): OrderImageSection[] => {
    const categoryItems = items.filter((item) => item.category === category);
    if (categoryItems.length === 0) return [];

    const rows: OrderImageRow[] = [];
    for (let index = 0; index < categoryItems.length; index += GRID_COLUMNS) {
      const rowItems = categoryItems.slice(index, index + GRID_COLUMNS).map((item) => ({
        item,
        lines: splitOrderItemName(item.name),
      }));
      const maxLines = Math.max(...rowItems.map(({ lines }) => lines.length), 1);
      rows.push({
        items: rowItems,
        height: Math.max(CELL_MIN_HEIGHT, (maxLines * CELL_LINE_HEIGHT) + CELL_VERTICAL_PADDING),
      });
    }

    const rowsHeight = rows.reduce((total, row) => total + row.height, 0)
      + (Math.max(0, rows.length - 1) * GRID_GAP_Y);
    return [{
      category,
      label: CATEGORY_LABELS[category],
      rows,
      height: SECTION_HEADING_HEIGHT + rowsHeight,
    }];
  });
  const gridHeight = sections.reduce((total, section) => total + section.height, 0)
    + (Math.max(0, sections.length - 1) * SECTION_GAP_Y);
  return {
    width: ORDER_IMAGE_WIDTH,
    height: Math.max(ORDER_IMAGE_MIN_HEIGHT, GRID_TOP + gridHeight + FOOTER_HEIGHT),
    cellWidth,
    sections,
  };
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("图片生成失败，请再试一次"));
    }, "image/png");
  });
}

function safeFilePart(value: string): string {
  return value.replace(/[\\/:*?"<>|]/gu, "").trim().slice(0, 20) || "我的";
}

function font(context: CanvasRenderingContext2D, weight: number, size: number, family: string): void {
  context.font = `${weight} ${size}px ${family}`;
}

function fitTextSize(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  startSize: number,
  minimumSize: number,
  weight: number,
  family: string,
): number {
  let size = startSize;
  while (size > minimumSize) {
    font(context, weight, size, family);
    if (context.measureText(text).width <= maxWidth) return size;
    size -= 2;
  }
  font(context, weight, minimumSize, family);
  return minimumSize;
}

function drawPaperTexture(context: CanvasRenderingContext2D, width: number, height: number): void {
  context.save();
  context.fillStyle = "rgb(239 81 47 / 0.045)";
  const marks = Math.max(180, Math.floor(height / 5));
  for (let index = 0; index < marks; index += 1) {
    const x = (index * 83) % width;
    const y = (index * 137) % height;
    const length = 1 + (index % 4);
    context.fillRect(x, y, length, 1);
  }
  context.restore();
}

function drawDottedFrame(context: CanvasRenderingContext2D, top: number, bottom: number, width: number): void {
  context.save();
  context.strokeStyle = ACCENT;
  context.lineWidth = 5;
  context.setLineDash([5, 10]);
  [52, width - 52].forEach((x) => {
    context.beginPath();
    context.moveTo(x, top);
    context.lineTo(x, bottom);
    context.stroke();
  });
  context.restore();
}

function drawLeaf(context: CanvasRenderingContext2D, x: number, y: number, scale: number): void {
  context.save();
  context.translate(x, y);
  context.scale(scale, scale);
  context.fillStyle = ACCENT;
  context.beginPath();
  context.moveTo(0, 22);
  context.bezierCurveTo(18, -4, 48, -10, 70, 6);
  context.bezierCurveTo(42, 16, 18, 34, 0, 22);
  context.fill();
  context.beginPath();
  context.moveTo(54, 18);
  context.bezierCurveTo(72, 2, 98, 4, 112, 24);
  context.bezierCurveTo(86, 31, 68, 29, 54, 18);
  context.fill();
  context.restore();
}

function loadIllustration(path: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("菜单插画加载失败，请再试一次"));
    image.src = path;
  });
}

function drawMenuItem(
  context: CanvasRenderingContext2D,
  lines: readonly string[],
  x: number,
  y: number,
  width: number,
): void {
  context.fillStyle = ACCENT;
  font(context, 800, 31, BODY_FONT);
  lines.forEach((line, lineIndex) => {
    context.fillText(line, x, y + 34 + (lineIndex * CELL_LINE_HEIGHT), width);
  });
}

function drawCategoryHeading(
  context: CanvasRenderingContext2D,
  label: string,
  itemCount: number,
  y: number,
  width: number,
): void {
  const number = String(itemCount).padStart(2, "0");
  context.fillStyle = INK;
  context.textAlign = "left";
  font(context, 900, 42, CHINESE_DISPLAY_FONT);
  context.fillText(label, IMAGE_PADDING, y + 45);
  const labelWidth = context.measureText(label).width;

  context.fillStyle = ACCENT;
  context.textAlign = "right";
  font(context, 800, 20, BODY_FONT);
  context.fillText(number, width - IMAGE_PADDING, y + 40);
  const numberWidth = context.measureText(number).width;

  context.save();
  context.strokeStyle = "rgb(239 81 47 / 0.66)";
  context.lineWidth = 2;
  context.setLineDash([4, 8]);
  context.beginPath();
  context.moveTo(IMAGE_PADDING + labelWidth + 24, y + 32);
  context.lineTo(width - IMAGE_PADDING - numberWidth - 24, y + 32);
  context.stroke();
  context.restore();
  context.textAlign = "left";
}

export async function generateOrderImage(
  guestName: string,
  items: readonly GuestOrderItem[],
  generatedAt = new Date(),
): Promise<GeneratedOrderImage> {
  const [eatingIllustration, runnerIllustration] = await Promise.all([
    loadIllustration(EATING_ILLUSTRATION_PATH),
    loadIllustration(RUNNER_ILLUSTRATION_PATH),
    document.fonts?.ready,
  ]);
  const layout = calculateOrderImageLayout(items);
  const canvas = document.createElement("canvas");
  canvas.width = layout.width;
  canvas.height = layout.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("图片生成失败，请再试一次");

  context.fillStyle = PAPER;
  context.fillRect(0, 0, layout.width, layout.height);
  drawPaperTexture(context, layout.width, layout.height);

  context.textBaseline = "alphabetic";
  context.textAlign = "center";
  context.fillStyle = ACCENT;
  font(context, 800, 22, BODY_FONT);
  context.fillText("XIAOYANG HOME KITCHEN", layout.width / 2, 58);

  fitTextSize(context, "MENU", layout.width - 126, 226, 164, 900, DISPLAY_FONT);
  context.fillText("MENU", layout.width / 2, 264);

  context.fillStyle = INK;
  fitTextSize(context, `${guestName}的点菜单`, layout.width - 250, 48, 34, 900, CHINESE_DISPLAY_FONT);
  context.fillText(`${guestName}的点菜单`, layout.width / 2, 332);

  const dateText = new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(generatedAt);
  context.fillStyle = MUTED_INK;
  font(context, 700, 22, BODY_FONT);
  context.fillText(`${dateText} / 共 ${items.length} 道`, layout.width / 2, 378);

  context.textAlign = "left";
  context.fillStyle = ACCENT;
  font(context, 900, 50, CHINESE_DISPLAY_FONT);
  context.fillText("今天想吃这些", IMAGE_PADDING, 470);
  drawLeaf(context, 808, 430, 0.9);

  const frameBottom = layout.height - 88;
  drawDottedFrame(context, 410, frameBottom, layout.width);

  let sectionY = GRID_TOP;
  layout.sections.forEach((section) => {
    const itemCount = section.rows.reduce((total, row) => total + row.items.length, 0);
    drawCategoryHeading(context, section.label, itemCount, sectionY, layout.width);
    let rowY = sectionY + SECTION_HEADING_HEIGHT;
    section.rows.forEach((row) => {
      row.items.forEach(({ lines }, columnIndex) => {
        const x = IMAGE_PADDING + (columnIndex * (layout.cellWidth + GRID_GAP_X));
        drawMenuItem(context, lines, x, rowY, layout.cellWidth);
      });
      rowY += row.height + GRID_GAP_Y;
    });
    sectionY += section.height + SECTION_GAP_Y;
  });

  const menuBottom = sectionY - (layout.sections.length > 0 ? SECTION_GAP_Y : 0);
  const artTop = Math.max(menuBottom + 90, layout.height - 520);
  context.drawImage(eatingIllustration, 84, artTop + 30, 320, 382);
  context.drawImage(runnerIllustration, 612, artTop - 4, 370, 429);

  context.fillStyle = MUTED_INK;
  font(context, 700, 20, BODY_FONT);
  context.fillText("来我家，记得带上好胃口", IMAGE_PADDING, layout.height - 58);
  context.textAlign = "right";
  context.fillStyle = ACCENT;
  context.fillText("今天吃什么 / design by 小羊", layout.width - IMAGE_PADDING, layout.height - 58);

  return {
    blob: await canvasToBlob(canvas),
    fileName: `${safeFilePart(guestName)}的点菜单.png`,
    width: layout.width,
    height: layout.height,
  };
}
